/**
 * ==========================================================================
 *  Everybody in the venue, placed and then animated.
 *
 *  `src/render/People.ts` builds the bodies; this decides where each one
 *  stands and what it does afterwards. Four things happen after the build:
 *  the awake ones turn to follow Kimmo, the crowd in front of the tent jumps
 *  to a record that has been playing since the page loaded, Mäysä and the
 *  porukka step out of the runway when the run earns it, and the whole lot
 *  turns round and scowls when the cable comes out.
 *
 *  Visuals only, and read-only with respect to the simulation. Nothing here
 *  is ever read back: `src/sim` has never been told there is anybody in the
 *  tent, and the two who *do* block the way - Mäysä in the porstua and the
 *  porukka on the floor - are colliders the simulation owns, which this only
 *  ever poses to match.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import { GREETING_REST, greetingPose } from './Greeting';
import { armDrop, dancerSeeds, jumpHeight, type DancerSeed } from './Dance';
import {
  CHEER_ELBOW,
  CHEER_SPLAY,
  MAYSA_BLOCKING_ELBOW,
  POINTING_ELBOW,
  sitterFigure,
  sleeperFigure,
} from './People';
import { flatSprawlerFigure, sprawlerFigure, standerFigure } from './PeopleUpright';
import { faceKimmo, pointArm, poseGreetingArm, raiseArms } from './Pose';
import type { SceneAssets } from './SceneAssets';
import { mulberry32 } from '../utils/prng';
import type { Vec3 } from '../utils/math';
import {
  boxCenter,
  sleeperBox,
  type LevelDefinition,
  type MaysaSpec,
  type StanderSpec,
} from '../world/Level';

/**
 * Where the crowd's *timing* is rolled from - a different stream from the one
 * their faces and their arm heights come out of, because how high a man holds
 * his hands and how far behind the beat he lands have nothing to do with each
 * other, and one seed for both would tie them together where nobody would
 * think to look for it.
 *
 * One stream for the whole crowd rather than one per man: `dancerSeeds` spaces
 * them against each other, so it has to see them together.
 */
const DANCE_SEED = 601;

/**
 * `sleeperFigure`'s index for the player's own body - a number of its own
 * rather than an index into `sleepers`, so that adding or removing a body in
 * the juhlasali cannot change the colour of the shirt Kimmo woke up in.
 */
const SLEEPING_PLAYER_SEED = 101;

export class Crowd {
  /** Filled during `build`; the caller puts these in the scene. */
  private out: THREE.Object3D[] = [];
  /** The player asleep in his own bed, on the READY screen. */
  private sleepingPlayerGroup: THREE.Group | null = null;
  /** Mäysä, and which of his two poses he is currently in. See `MaysaSpec`. */
  private maysaGroup: THREE.Group | null = null;
  private maysaAside: boolean | null = null;
  /**
   * The porukka out on the juhlasali's floor, and which of their two poses
   * they are all in. One flag for the seven of them rather than one each,
   * because they move as one thing: the runway is shut or it is not, and there
   * is no state in which four of them have stepped aside.
   */
  private readonly porukkaGroups: THREE.Group[] = [];
  private porukkaAside: boolean | null = null;
  /** The empty tuoppi off the top of the stack, hidden when Kimmo picks it up. */
  private readonly simaGlassParts: THREE.Object3D[] = [];
  /**
   * The man at the sauna door, in the two pieces a wave is actually made of,
   * and the write-once latch on the far end of it. `greetingPose` stops
   * answering when the arm is back down, and `greetingUp` is what stops the
   * rest pose being written into a man nobody has spoken to on every frame.
   */
  private greeterArm: THREE.Object3D | null = null;
  private greeterElbow: THREE.Object3D | null = null;
  private greetingUp = false;
  /**
   * The people in front of the rig: everybody `raiseArms` got to, with the
   * arms it raised and the beat it rolled them. Flat, and holding the objects
   * rather than the names: this is walked every frame and the answer never
   * changes.
   */
  private readonly dancers: {
    figure: THREE.Object3D;
    seed: DancerSeed;
    rotationY: number;
    arms: { arm: THREE.Object3D; lift: number }[];
  }[] = [];
  /** The DJ keeps his mixer pose until the power fails. */
  private dj: { figure: THREE.Object3D; spec: StanderSpec; index: number } | null = null;
  /** Awake standers; dancers and the DJ keep their own animation. */
  private readonly watchingStanders = new Map<THREE.Object3D, {
    head: THREE.Object3D | undefined;
    points?: [number, number, number];
    elbowBend: number;
  }>();
  private powerOn = true;

  constructor(
    private readonly assets: SceneAssets,
    private readonly level: LevelDefinition,
  ) {}

  /** Everybody, for the caller to put in the scene. */
  build(): THREE.Object3D[] {
    this.out = [];
    this.buildSleepers();
    this.buildSleepingPlayer();
    this.buildSitters();
    this.buildSprawlers();
    this.buildStanders();
    this.buildMaysa();
    this.buildPorukka();
    const built = this.out;
    this.out = [];
    return built;
  }

  /** The cook is built from his own pan, and joins the watchers here. */
  watchCook(figure: THREE.Object3D): void {
    this.watchKimmo(figure);
  }

  /**
   * The blackout, as it reaches the people: the rig is hidden because a lamp
   * that is off is a lamp you cannot see, but a man is still standing there
   * with the music gone, so he is put back on the grass with his hands down
   * and turned round to scowl at whoever pulled the cable.
   */
  setPowerOn(on: boolean): void {
    this.powerOn = on;
    for (const dancer of this.dancers) {
      dancer.figure.rotation.y = dancer.rotationY + (on ? 0 : Math.PI);
      const face = dancer.figure.getObjectByName('kasvot');
      const angryFace = dancer.figure.getObjectByName('kasvot-sahkokatko');
      if (face) face.visible = on;
      if (angryFace) angryFace.visible = !on;
      for (const { arm } of dancer.arms) {
        const side = arm.name === 'kasi-vasen' ? 1 : -1;
        arm.rotation.z = on ? side * CHEER_SPLAY : 0;
        const elbow = arm.getObjectByName('kyynarpaa');
        if (elbow) elbow.rotation.x = on ? CHEER_ELBOW : 0;
      }
    }
    if (this.dj) {
      const { figure, spec, index } = this.dj;
      figure.rotation.y = spec.rotationY;
      const face = figure.getObjectByName('kasvot');
      const sadFace = figure.getObjectByName('kasvot-sahkokatko');
      if (face) face.visible = on;
      if (sadFace) sadFace.visible = !on;
      if (on) {
        for (const name of ['kasi-oikea', 'kasi-vasen']) {
          const arm = figure.getObjectByName(name);
          arm?.rotation.set(0, 0, 0, 'XYZ');
          arm?.getObjectByName('kyynarpaa')?.rotation.set(0, 0, 0);
        }
        if (spec.points) pointArm(figure, spec.points);
      } else {
        raiseArms(figure, index);
      }
    }
    this.renderCrowd(0);
  }

  /** The glass in Kimmo's hand, and the seven who are standing in his way. */
  setSima(view: { taken: boolean; poured: boolean } | null): void {
    if (!this.level.sima) return;
    this.posePorukka(view?.poured === true);
    const onTable = !(view?.taken ?? false);
    for (const part of this.simaGlassParts) part.visible = onTable;
  }

  /** Mäysä steps out of the porstua once the plate is full. */
  setAamupalaDone(done: boolean): void {
    const spec = this.level.aamupala?.maysa;
    if (spec) this.poseMaysa(spec, done);
  }

    /**
     * The bodies you have to get past.
     *
     * These used to be one capsule each, which from a metre away reads as a bin
     * bag rather than as somebody you are hurdling - and the whole joke of the
     * juhlasali slalom is that they are *people*. So: head, torso, hips, two arms
     * and two legs, laid out along the body's own axis.
     *
     * The figure is drawn narrower than its collider on purpose. The collider is
     * 0.85 m across because a generous box is what keeps the slalom from feeling
     * unfair; a person is not, and drawing one 0.85 m wide would look like a
     * mattress. Nothing here touches `sleeperBox()`, so the shapes can be
     * reworked freely without moving a single collision surface.
     */
    private buildSleepers(): void {
      this.level.sleepers.forEach((spec, index) => {
        const box = sleeperBox(spec);
        const center = boxCenter(box);
        const figure = sleeperFigure(this.assets, spec, index);
        // Built lying along local +Z with the head at the far end, so a body that
        // lies along X is the same figure, turned.
        figure.rotation.y = spec.orientation === 'x' ? Math.PI / 2 : 0;
        figure.position.set(center.x, box.minY, center.z);
        figure.name = spec.name;
        this.out.push(figure);
      });
    }

    /**
     * Kimmo, still asleep in his own bed, for as long as nobody has woken him.
     *
     * Built like any other sleeper and placed like none of them: he is turned
     * end for end, because `sleeperFigure` puts the head at the body's +Z end
     * and the pillow is at the bed's -Z end. Juppe across the room lies the way
     * the hall's bodies do; this one is looked at from a metre away on the
     * READY screen, so it has to be the way a person actually lies in a bed.
     *
     * The seed is a number of its own rather than an index into `sleepers`, so
     * that adding or removing a body in the juhlasali cannot change the colour
     * of the shirt Kimmo woke up in.
     */
    private buildSleepingPlayer(): void {
      const spec = this.level.sleepingPlayer;
      if (!spec) return;

      const box = sleeperBox(spec);
      const center = boxCenter(box);
      const group = sleeperFigure(this.assets, spec, SLEEPING_PLAYER_SEED);
      group.rotation.y = spec.orientation === 'x' ? -Math.PI / 2 : Math.PI;
      group.position.set(center.x, box.minY, center.z);
      group.name = spec.name;
      this.sleepingPlayerGroup = group;
      this.out.push(group);
    }

    /** The ones who are awake. Drawn and nothing else - see `SitterSpec`. */
    private buildSitters(): void {
      this.level.sitters.forEach((spec, index) => {
        const figure = sitterFigure(this.assets, spec, index);
        figure.position.set(spec.seat[0], 0, spec.seat[1]);
        figure.rotation.y = spec.rotationY;
        figure.name = spec.name;
        this.out.push(figure);
      });
    }

    /** The ones who are neither asleep in a bed nor sat up. See `SprawlerSpec`. */
    private buildSprawlers(): void {
      this.level.sprawlers.forEach((spec, index) => {
        const figure =
          spec.pose === 'flat'
            ? flatSprawlerFigure(this.assets, spec, index)
            : sprawlerFigure(this.assets, spec, index);
        figure.position.set(spec.hips[0], 0, spec.hips[1]);
        figure.rotation.y = spec.rotationY;
        figure.name = spec.name;
        this.out.push(figure);
      });
    }

    /**
     * The standing figures the level places on their own.
     *
     * The cook is not one of them: he comes attached to his pan and is drawn by
     * `addCook`, so the seeds here start at 1 and his face stays his own however
     * many people end up standing in the hall.
     */
    private buildStanders(): void {
      const crowd: { figure: THREE.Object3D; arms: { arm: THREE.Object3D; lift: number }[] }[] = [];
      this.level.standers.forEach((spec, index) => {
        const figure = standerFigure(this.assets, spec, index + 1);
        figure.name = spec.name;
        figure.position.set(spec.stand[0], 0, spec.stand[1]);
        figure.rotation.y = spec.rotationY;
        this.out.push(figure);
        if (spec.points) pointArm(figure, spec.points);
        else if (spec.cheering) crowd.push({ figure, arms: raiseArms(figure, index + 1) });
        if (spec.name === 'dj-solidi') this.dj = { figure, spec, index: index + 1 };
        else if (!spec.cheering) this.watchKimmo(figure, spec.points);
        // And the one man in the hall who has an arm to put up later. Looked up
        // here rather than on the frame the handle goes, because the frame the
        // handle goes is not the place to be walking a scene graph.
        if (spec.name === this.level.sauna?.greeter) this.holdGreeter(figure);
      });

      // And the beat they are moved on, once it is known how many of them there
      // are. That is the whole reason this is not done inside the loop above:
      // `dancerSeeds` spaces the crowd against itself, so it cannot answer for
      // one man at a time. See `renderCrowd`.
      const seeds = dancerSeeds(crowd.length, mulberry32(0x51eeb0 + DANCE_SEED * 977));
      crowd.forEach((member, index) => this.dancers.push({
        ...member, seed: seeds[index], rotationY: member.figure.rotation.y,
      }));
    }

    /**
     * Remember the arm the man at the sauna door greets with.
     *
     * His right arm is on the door side. `GREETING_SIDE` is the same -1
     * `raiseArms` gives `kasi-oikea`, so the slight outward turn points toward
     * the door rather than across his chest.
     */
    private holdGreeter(figure: THREE.Object3D): void {
      const arm = figure.getObjectByName('kasi-oikea');
      if (!arm) return;
      this.greeterArm = arm;
      this.greeterElbow = arm.getObjectByName('kyynarpaa') ?? null;
    }

    /**
     * The hand at the sauna door, `age` seconds after the handle was tried -
     * or null when nobody has tried it yet this run.
     *
     * Simulation seconds rather than render seconds, unlike `renderCrowd` next
     * door: the crowd in the tent is dancing to a record that has been playing
     * since the page loaded, and this is a man answering a question that was
     * asked on a known step. So a pause holds his hand up where it is instead
     * of running the gesture out behind a menu, and a restart puts it down.
     *
     * Read-only, like everything else in here. `Sauna.interact()` is empty and
     * stays empty; this is what that emptiness looks like.
     */
    renderGreeting(age: number | null): void {
      const arm = this.greeterArm;
      if (!arm) return;
      const pose = age === null ? null : greetingPose(age);
      if (pose === null) {
        // One write on the way out and then nothing, for the whole rest of the
        // run. See `greetingUp`.
        if (!this.greetingUp) return;
        this.greetingUp = false;
        poseGreetingArm(arm, this.greeterElbow, GREETING_REST);
        return;
      }
      this.greetingUp = true;
      poseGreetingArm(arm, this.greeterElbow, pose);
    }

    /**
     * Render-rate update: the crowd in front of the rig jumps, and its hands
     * come down and go back up. `src/render/Dance.ts` owns both curves; this end
     * is three.js and nothing else.
     *
     * Off the same wall-clock as `renderDisco` and for the same reason - the
     * party started before the page loaded and does not rewind because somebody
     * pressed R - and answering the blackout the same way it does, one step
     * further on. The rig is *hidden* when the cable comes out, because a lamp
     * that is off is a lamp you cannot see; a man is still standing there with
     * the music gone, so he is put back on the grass with his hands down.
     * `setPowerOn` switches his face to a scowl; he then turns to keep watching
     * Kimmo's interpolated position without leaving his spot.
     *
     * Visuals only. Nothing here is read back by anything, and nothing in
     * `src/sim` has ever been told there is anybody in the tent.
     */
    renderCrowd(renderTime: number, playerPosition?: Readonly<Vec3>): void {
      if (!this.powerOn && playerPosition && this.dj) {
        const { figure } = this.dj;
        const dx = playerPosition.x - figure.position.x;
        const dz = playerPosition.z - figure.position.z;
        if (dx !== 0 || dz !== 0) figure.rotation.y = Math.atan2(dx, dz);
      }
      for (const dancer of this.dancers) {
        dancer.figure.position.y = this.powerOn ? jumpHeight(dancer.seed, renderTime) : 0;
        if (!this.powerOn && playerPosition) {
          const dx = playerPosition.x - dancer.figure.position.x;
          const dz = playerPosition.z - dancer.figure.position.z;
          // Keep the last direction if Kimmo is exactly on the figure's centre.
          if (dx !== 0 || dz !== 0) dancer.figure.rotation.y = Math.atan2(dx, dz);
        }
        const drop = this.powerOn ? armDrop(dancer.seed, renderTime) : 0;
        // The roll and the splay are the pose's and stay put: an arm that is
        // pumping is an arm going up and down, not one waving about. Only the
        // lift moves, which is also the only one of the three anybody sees from
        // the far side of the apron.
        for (const { arm, lift } of dancer.arms) {
          arm.rotation.x = this.powerOn ? -(Math.PI / 2 + lift - drop) : 0;
        }
      }
    }

    private watchKimmo(
      figure: THREE.Object3D,
      points?: [number, number, number],
      elbowBend = POINTING_ELBOW,
    ): void {
      this.watchingStanders.set(figure, { head: figure.getObjectByName('paa'), points, elbowBend });
    }

    /** Use the rendered eye so looking up at a jump or down at the seat follows Kimmo. */
    renderStanders(eye: Readonly<Vec3>): void {
      for (const [figure, { head, points, elbowBend }] of this.watchingStanders) {
        faceKimmo(figure, head, eye);
        if (points) pointArm(figure, points, elbowBend);
      }
    }

    /**
     * ==========================================================================
     *  MÄYSÄ, AND THE ONE THING IN THE VENUE THAT MOVES BECAUSE OF A DECISION
     * ==========================================================================
     *  Everybody else drawn out of `Figure` is where the level says they are and
     *  stays there. This one has two places he can be - across the porstua, and
     *  out of it - and which one is true is a fact about the run rather than
     *  about the level, so it is set from `setAamupala` and never from here.
     *
     *  One figure, moved. Two would be two men with the same shirt on: the face
     *  and the skin tone come out of the seed, and a second entry would roll a
     *  second person. See `MaysaSpec`.
     * ==========================================================================
     */
    private buildMaysa(): void {
      const spec = this.level.aamupala?.maysa;
      if (!spec) return;
      // After the level's own standers, so putting one more man in the hall
      // cannot change what this one's face looks like - the same reason the cook
      // has index 0 to himself.
      const figure = standerFigure(this.assets, spec, this.level.standers.length + 1);
      figure.name = spec.name;
      this.out.push(figure);
      this.maysaGroup = figure;
      this.poseMaysa(spec, false);
    }

    /** Puts him in one of his two poses, and aims the arm that goes with it. */
    private poseMaysa(spec: MaysaSpec, aside: boolean): void {
      const figure = this.maysaGroup;
      if (!figure || this.maysaAside === aside) return;
      this.maysaAside = aside;

      const pose = aside ? spec.aside : spec;
      figure.position.set(pose.stand[0], 0, pose.stand[1]);
      figure.rotation.y = pose.rotationY;

      // Both arms back down before the new one goes up. It happens to be the
      // left one in both poses - the pan is round his left in the doorway and
      // the door is round his left out on the kivetys - but that falls out of
      // where he is standing rather than out of a decision, and an arm left
      // where it was is a man directing traffic in two directions at once.
      for (const name of ['kasi-vasen', 'kasi-oikea']) {
        const arm = figure.getObjectByName(name);
        if (!arm) continue;
        arm.rotation.set(0, 0, 0);
        const elbow = arm.getObjectByName('kyynarpaa');
        if (elbow) elbow.rotation.x = 0;
      }
      if (pose.points) {
        pointArm(
          figure,
          pose.points,
          aside ? POINTING_ELBOW : MAYSA_BLOCKING_ELBOW,
        );
      }
      this.watchKimmo(figure, pose.points, aside ? POINTING_ELBOW : MAYSA_BLOCKING_ELBOW);
    }

    /**
     * ==========================================================================
     *  THE PORUKKA, AND THE SECOND THING IN THE VENUE THAT MOVES
     * ==========================================================================
     *  Mäysä above is one man with two places to stand. This is seven of them,
     *  and the only thing different about it is the arithmetic: which pose is
     *  true is a fact about the run rather than about the level, so it is set
     *  from `setSima` and never from here.
     *
     *  Their seeds start after Mäysä's, which is itself after the level's own
     *  standers - so the cook, the orga, the DJ and the man in the porstua all
     *  keep the faces they had, and adding an eighth person to this crowd cannot
     *  change any of the seven in front of him either.
     * ==========================================================================
     */
    private buildPorukka(): void {
      const crowd = this.level.sima?.crowd;
      if (!crowd) return;
      const seed = this.level.standers.length + 2;
      crowd.forEach((spec, index) => {
        const figure = standerFigure(this.assets, spec, seed + index);
        figure.name = spec.name;
        this.out.push(figure);
        this.porukkaGroups.push(figure);
      });
      this.posePorukka(false);
    }

    /**
     * Puts all seven in one of their two poses, and aims the one arm among them
     * that is doing anything.
     *
     * The move is a cut rather than a walk, and it is now a cut that can be
     * caught: they stand four metres east of the hanat rather than a whole hall
     * away, so the frame they move on is a frame a player who turns round from
     * his own pour is looking at. It stays a cut because the pour is made facing
     * the counter and the room is behind you while you make it. See
     * `Sima.simulate` for the rest of that argument.
     */
    private posePorukka(aside: boolean): void {
      const crowd = this.level.sima?.crowd;
      if (!crowd || this.porukkaAside === aside) return;
      this.porukkaAside = aside;

      crowd.forEach((spec, index) => {
        const figure = this.porukkaGroups[index];
        if (!figure) return;
        const pose = aside ? spec.aside : spec;
        figure.position.set(pose.stand[0], 0, pose.stand[1]);
        figure.rotation.y = pose.rotationY;

        // Both arms down before either goes up, exactly as Mäysä's do: the one
        // who points is pointing at the baari in one pose and at Kimmo's machine
        // in the other, and those are round opposite shoulders.
        for (const name of ['kasi-vasen', 'kasi-oikea']) {
          const arm = figure.getObjectByName(name);
          if (!arm) continue;
          arm.rotation.set(0, 0, 0);
          const elbow = arm.getObjectByName('kyynarpaa');
          if (elbow) elbow.rotation.x = 0;
        }
        if (pose.points) {
          pointArm(figure, pose.points);
        }
        this.watchKimmo(figure, pose.points);
      });
    }

    /**
     * The pieces of the one glass in the hall that can be picked up.
     *
     * Gathered after `buildCylinders`, because that is what makes them - a
     * tuoppi is a stack of two or three named cylinders (`tuoppi()` in
     * src/config/level.ts) and this is all of them, whatever the pour left.
     *
     * It is the top glass of one of the piles on the tuoppipöytä and only that
     * one is named `simatuoppi-*`, so what is left standing when it goes is the
     * rest of the pile - a stack one glass shorter rather than a table that has
     * been cleared. See `TUOPPIPOYTA`.
     */
    collectSimaGlass(scene: THREE.Object3D): void {
      if (!this.level.sima) return;
      for (const spec of this.level.cylinders) {
        if (!spec.name.startsWith('simatuoppi-')) continue;
        const mesh = scene.getObjectByName(spec.name);
        if (mesh) this.simaGlassParts.push(mesh);
      }
    }

    /**
     * Whether Kimmo is still in his bed. True on the READY screen, false from
     * the frame the run starts - he is up, so the bed is empty.
     */
    setSleepingPlayerVisible(visible: boolean): void {
      if (this.sleepingPlayerGroup) this.sleepingPlayerGroup.visible = visible;
    }
}
